import React, { Dispatch, SetStateAction, memo, useCallback, useEffect, useState } from 'react'
import { useWatch } from 'react-hook-form'
import { stringifyUrl } from 'query-string'
import { ControlledHiddenInput } from 'transfix-form/components'
import ControlledCurrencyInput from 'transfix-form/components/ControlledCurrencyInput'
import ControlledPhoneInput from 'transfix-form/components/ControlledPhoneInput'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import AddIcon from 'transfix-icons/build/AddIcon'
import Button from 'transfix-ui/components/Button'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { IUser, Maybe } from 'types/graphqlTypes'
import { ICarrierResultFragment } from 'apps/marketplace/components/CarrierAutosuggest/graphql/CarrierResultFragment'
import ControlledCarrierUserAutosuggest from 'apps/marketplace/pages/shipment-details/components/forms/controlled/ControlledCarrierUserAutosuggest'
import { IShipmentCoverActorFragment } from '../../../graphql/ShipmentActorFragment'
import { useCoverModalFormContext } from '../../../hooks/useCoverModalForm'
import { ICoverInfoCarrier } from '../../../types'
import CarrierAutosuggestWithCreate from '../../CarrierAutosuggestWithCreate'

const useCarrierInformationFormGroupStyles = makeStyles(theme => ({
  boxWithSeparator: {
    marginBottom: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    borderBottom: '1px solid',
    borderBottomColor: brandColors.coolGray4,
  },
  buttonWrapper: {
    marginTop: theme.spacing(1),
  },
  datePicker: {
    '& input': {
      height: '1.875rem',
    },
  },
  bigTimePicker: {
    '& .text-input-container': {
      width: '100%',
    },
  },
  fieldWrapper: {
    marginTop: theme.spacing(2),
  },
  hr: {
    backgroundColor: brandColors.coolGray4,
    margin: `${theme.spacing(4)} 0`,
  },
}))

export interface ICarrierInformationFormGroupProps {
  selectedCarrier: Maybe<ICoverInfoCarrier>
  onSelectCarrier: (carrier: Maybe<ICoverInfoCarrier>) => void
  setSelectedContactUserId: Dispatch<SetStateAction<string | undefined>>
  isCovered?: boolean
  dispatcher: Maybe<IShipmentCoverActorFragment>
}

const CarrierInformationFormGroup = memo<ICarrierInformationFormGroupProps>(
  ({ dispatcher, isCovered, onSelectCarrier, selectedCarrier, setSelectedContactUserId }) => {
    const classes = useCarrierInformationFormGroupStyles()
    /**
     * Form Context
     */
    const { control, errors, register, setValue } = useCoverModalFormContext()
    const bookingContactName = useWatch<string>({ control, name: 'bookingContactName' })

    const [carrierContact, setCarrierContact] = useState<Maybe<IShipmentCoverActorFragment>>(
      dispatcher
    )
    const { email: carrierContactEmail } = carrierContact || {}

    const {
      dotNumber: carrierDotNumber,
      id: carrierId,
      mcNumber: carrierMcNumber,
      name: carrierName,
    } = selectedCarrier || { id: '' }

    const handleCreateCarrier = (newCarrier: ICarrierResultFragment) => {
      const { id: newCarrierId } = newCarrier || {}
      setValue('carrierApplicationId', newCarrierId)
      onSelectCarrier(newCarrier)
    }

    const userSelectHandler = useCallback(
      (user: IUser | null) => {
        const { cellPhone, email, fullName, id: userId, phone, phoneExt } = user || {}
        setCarrierContact(user)
        setSelectedContactUserId(userId)
        setValue('bookingContactName', fullName || '', { shouldValidate: true })
        register('bookingContactEmail')
        setValue('bookingContactEmail', email || '', { shouldValidate: true })
        setValue('bookingContactPhoneExt', phoneExt || '')
        setValue('bookingContactPhone', cellPhone || phone || '', { shouldValidate: true })
      },
      [setCarrierContact, setSelectedContactUserId, setValue, register]
    )

    const userInputChangeHandler = useCallback(
      (value: string) => {
        setValue('bookingContactName', value, { shouldValidate: true })
      },
      [setValue]
    )

    const addNewUserHandler = useCallback(() => {
      const addUserUrl = stringifyUrl({
        url: `${__ROOT__}/users/new`,
        query: {
          orgType: 'carrier',
          orgId: carrierId || '',
          orgName: carrierName || '',
          orgMcNumber: carrierMcNumber || '',
          orgDotNumber: carrierDotNumber || '',
        },
      })
      window.open(addUserUrl, '_blank')
    }, [carrierId, carrierName, carrierMcNumber, carrierDotNumber])

    /**
     * set carrier contact once dispatcher is loaded
     */
    useEffect(() => {
      if (dispatcher) {
        setCarrierContact(dispatcher)
      }
    }, [dispatcher, setCarrierContact])
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant='subtitle1'>Carrier Information</Typography>
        </Grid>
        <Grid item xs={12}>
          <div data-test='sd-coverage-form-carrier'>
            <CarrierAutosuggestWithCreate
              name='carrierApplicationId'
              control={control}
              defaultValue={carrierId}
              carrier={selectedCarrier}
              onCarrierChange={onSelectCarrier}
              onCreate={handleCreateCarrier}
              AutocompleteProps={{
                required: true,
                size: 'medium',
                disabled: isCovered,
                inputError: !!errors?.carrierApplicationId?.message,
                renderInputType: 'withIcon',
                inputIcon: 'Search',
              }}
            />
          </div>
        </Grid>
        {selectedCarrier && (
          <>
            <Grid item xs={12}>
              <ControlledHiddenInput control={control} name='bookingContactName' />
              <div data-test='sd-coverage-form-contact'>
                <ControlledCarrierUserAutosuggest
                  name='carrierContactId'
                  control={control}
                  carrierId={carrierId}
                  user={dispatcher}
                  onUserChange={userSelectHandler}
                  onInputChange={userInputChangeHandler}
                  defaultInputValue={bookingContactName}
                  AutocompleteProps={{
                    required: true,
                    label: 'Dispatcher Name',
                    inputError:
                      !!errors.carrierContactId?.message || !!errors.bookingContactName?.message,
                    /** The following properties are needed for freeSolo */
                    autoHighlight: false,
                    autoSelect: false,
                    autoComplete: false,
                    clearOnBlur: false,
                    clearOnEscape: false,
                    selectOnFocus: false,
                    freeSolo: true,
                    size: 'medium',
                    renderInputType: 'withIcon',
                    inputIcon: 'People',
                  }}
                />
              </div>
              <div className={classes.buttonWrapper}>
                <Button
                  variant='text'
                  startIcon={<AddIcon />}
                  onClick={addNewUserHandler}
                  noPadding>
                  Add User
                </Button>
              </div>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={8}>
                  <ControlledPhoneInput
                    control={control}
                    name='bookingContactPhone'
                    PhoneInputProps={{
                      dataTest: 'sd-coverage-form-contact-phone',
                      label: 'Dispatcher Phone',
                      clearable: true,
                      required: true,
                      large: true,
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <ControlledTextField
                    control={control}
                    name='bookingContactPhoneExt'
                    TextInputProps={{
                      label: 'Phone Ext',
                      dataTest: 'sd-coverage-form-booking-phone-ext-number',
                      placeholder: 'Ext.',
                      error: Boolean(errors.bookingContactPhoneExt),
                      validationMessage: errors.bookingContactPhoneExt?.message && '',
                      large: true,
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
        {carrierContact && (
          <Grid item xs={12}>
            <ControlledTextField
              control={control}
              name='bookingContactEmail'
              defaultValue={carrierContactEmail}
              TextInputProps={{
                dataTest: 'sd-coverage-form-contact-email',
                label: 'Contact Email',
                placeholder: 'Contact Email',
                error: !!errors.bookingContactEmail,
                disabled: true,
                large: true,
              }}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <ControlledCurrencyInput
            name='rate'
            control={control}
            CurrencyInputProps={{
              dataTest: 'sd-coverage-form-rate',
              placeholder: '$',
              label: 'Rate',
              required: true,
              invalid: !!errors.rate,
              hideRequiredLabel: true,
              clearable: true,
              large: true,
            }}
          />
        </Grid>
      </Grid>
    )
  }
)

export default CarrierInformationFormGroup
